import Image from "next/image"
import { useRouter } from "next/router"
import { AiOutlineCheckCircle } from "react-icons/ai"
import stateCityDictionary, {
  formatName,
  urlifyName,
} from "../../constants/stateCityDictionary"
import * as config from "../../util/config"
import { buildUrl } from "../../util/string"
import { Anchor } from "src/app/components/anchor"
import { safelyRenderHtml } from "../../util/safelyRenderHtml"
import { GoCheckCircle } from "react-icons/go"

/** Cost-section copy fields only — never `data`/`services` HTML blobs. */
export interface PetShippingCostCopy {
  costOfShipping?: string
  methodOfTransport?: string
  timingAndDistance?: string
  vipService?: string
}

export type PetShippingCostProps = {
  costEntry: PetShippingCostCopy | null
}

export default function PetShippingCost({ costEntry }: PetShippingCostProps) {
  const router = useRouter()
  const { city, state } = router.query
  const formattedState = formatName(state as string)
  const urlifiedCity = city ? urlifyName(city as string) : ""

  const originalCity =
    stateCityDictionary[formattedState]?.cities.find(
      (dictCity) => urlifyName(dictCity) === urlifiedCity,
    ) ?? ""

  const formattedCity = originalCity ? formatName(originalCity) : ""
  const stateAbbrev = stateCityDictionary[formattedState]?.abbreviation ?? ""
  const locationCityState = formattedCity
    ? `${formattedCity}, ${formattedState}`
    : formattedState

  const locationName = formattedCity
    ? `${formattedCity}, ${stateAbbrev}`
    : formattedState
  const appHost = config.getAppHost()

  return (
    <section className="w-full my-8 xl:my-16 flex xl:gap-20 md:gap-10">
      <article className="md:pl-8">
        <h3 className="mb-5 text-3xl font-merriweather leading-[44px]">
          What determines the cost of pet shipping in {locationCityState}?
        </h3>
        <div className="mb-8 text-xl font-inter">
          {safelyRenderHtml(
            costEntry?.costOfShipping ||
              "Costs vary by distance, timing, and your pet's needs. Each quote comes directly from a background-checked driver, and the price you see is what you pay.",
          )}
        </div>
        <section className="ml-16 md:ml-0 relative max-w-prose-3xl">
          <h4 className="absolute -left-16 text-5xl text-gold font-merriweather">
            1
          </h4>
          <h3 className="text-2xl text-inherit mb-4 font-merriweather">
            Method of Transport
          </h3>
          {costEntry?.methodOfTransport ? (
            <div className="text-xl font-inter">
              {safelyRenderHtml(costEntry.methodOfTransport)}
            </div>
          ) : (
            <>
              <p className="max-w-none font-inter prose-h4:text-xl leading-7 prose-h4:text-black prose-h4:font-normal prose-p:text-[16px]">
                First, you'll decide between air or pet ground transportation.
                Small animals can be checked as carry-on luggage on flights,
                which is the least expensive option — keep in mind you'll need
                to buy a ticket for your pet as well as yourself. Larger breeds
                must fly in the cargo area, which can cost as much as $1,000 or
                more depending on the pet shipping airline's policy.
              </p>
              <p className="max-w-none font-inter prose-h4:text-xl leading-7 prose-h4:text-black prose-h4:font-normal prose-p:text-[16px]">
                Save money on{" "}
                <Anchor
                  href={buildUrl(appHost, "/shipments/new/animals")}
                  className="text-link"
                >
                  {formattedCity} pet transport
                </Anchor>{" "}
                service with the CitizenShipper marketplace. Drivers bid on your
                transportation listing, lowering costs. Once you receive bids,
                read reviews and communicate directly with them to find the
                perfect match.
              </p>
            </>
          )}
        </section>
        <section className="ml-16 md:ml-0 relative max-w-prose-3xl">
          <h4 className="absolute -left-16 text-5xl text-gold font-merriweather">
            2
          </h4>
          <h3 className="text-2xl text-inherit mb-4 font-merriweather">
            Timing and Distance
          </h3>
          {costEntry?.timingAndDistance ? (
            <div className="font-inter max-w-none font-inter prose-h4:text-xl leading-7 prose-h4:text-black prose-h4:font-normal prose-p:text-[16px]">
              {safelyRenderHtml(costEntry.timingAndDistance)}
            </div>
          ) : (
            <>
              <p className="max-w-none font-inter prose-h4:text-xl leading-7 prose-h4:text-black prose-h4:font-normal prose-p:text-[16px]">
                {formattedCity} pet transport companies charge a per-mile rate
                for ground transport; the further the distance, the lower your
                pet transport cost per mile.
              </p>
              <p className="max-w-none font-inter prose-h4:text-xl leading-7 prose-h4:text-black prose-h4:font-normal prose-p:text-[16px]">
                The timing of the transport matters. Because summer and holidays
                are the busiest times of the year, pet shipping companies in{" "}
                {locationCityState} charge a premium due to limited
                availability.
              </p>
            </>
          )}
        </section>
        <section className="ml-16 md:ml-0 relative max-w-prose-3xl">
          <h4 className="absolute -left-16 text-5xl text-gold font-merriweather">
            3
          </h4>
          <h3 className="text-2xl text-inherit mb-4 font-merriweather">
            VIP Service
          </h3>
          {costEntry?.vipService ? (
            <div className="font-inter">
              {safelyRenderHtml(costEntry.vipService)}
            </div>
          ) : (
            <>
              <p className="font-inter">
                You can expect to pay extra for VIP service. Some transport
                companies in {locationName} transport your pet alone in a pet
                taxi. Why is VIP service preferential?
              </p>
              <ul className="p-0 font-inter">
                <li className="flex">
                  <span className="text-gold pr-4">
                    <GoCheckCircle size={24} />
                  </span>
                  <p className="grow">
                    VIP service maintains quarantine for young pets who aren't
                    fully vaccinated.
                  </p>
                </li>
                <li className="flex">
                  <span className="text-gold pr-4">
                    <GoCheckCircle size={24} />
                  </span>
                  <p className="grow">
                    Your pet rides alone with one dedicated driver for the
                    entire trip.
                  </p>
                </li>
                <li className="flex">
                  <span className="text-gold pr-4">
                    <GoCheckCircle size={24} />
                  </span>
                  <p className="grow">
                    You won't need to stress about the behavior of temperamental
                    dogs or cats.
                  </p>
                </li>
              </ul>
            </>
          )}
        </section>
      </article>
      <Image
        className="hidden lg:block mx-auto object-contain"
        src="/images/mobile-device.webp"
        alt={`pet transportation service ${city ? city + ", " : ""} ${state}`}
        width={300}
        height={668}
      />
    </section>
  )
}
